import React, { useEffect } from "react";
import { DualHeader } from "@/components/DualHeader";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { EmptyCart } from "@/components/cart/EmptyCart";
import { CartItem } from "@/components/cart/CartItem";
import { OrderSummary } from "@/components/cart/OrderSummary";
import { CartHeader } from "@/components/cart/CartHeader";
import { useCheckout } from "@/hooks/useCheckout";
import { AlertCircle, Plus } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AssistanceFooter } from "@/components/cart/AssistanceFooter";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const CartPage = () => {
  const { items, removeItem, clearCart, getTotal } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const { isProcessing, debugInfo, handleCheckout } = useCheckout();

  const handleRemoveItem = (id: string) => {
    removeItem(id);
  };

  const toggleItemDetails = (id: string) => {
    setExpandedItems(prev => 
      prev.includes(id) 
        ? prev.filter(itemId => itemId !== id) 
        : [...prev, id]
    );
  };

  // Enhanced customer info extraction to handle different cart item types and complete information
  const getCustomerInfo = () => {
    if (items.length === 0) {
      return { 
        email: "", 
        name: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        licensePlate: ""
      };
    }

    // Look for customer info in any cart item, prioritizing non-empty values
    let customerEmail = "";
    let customerName = "";
    let customerPhone = "";
    let customerAddress = "";
    let customerCity = "";
    let customerState = "";
    let customerZipCode = "";
    let customerLicensePlate = "";

    for (const item of items) {
      // Check if this item has valid customer email/name
      if (item.customerEmail && item.customerEmail.trim() && item.customerEmail !== "guest@example.com") {
        customerEmail = item.customerEmail.trim();
      }
      if (item.customerName && item.customerName.trim() && item.customerName !== "Guest User") {
        customerName = item.customerName.trim();
      }
      
      // Extract additional customer information from cart items
      if (item.phoneNumber && item.phoneNumber.trim()) {
        customerPhone = item.phoneNumber.trim();
      }
      if (item.address && item.address.trim()) {
        customerAddress = item.address.trim();
      }
      if (item.city && item.city.trim()) {
        customerCity = item.city.trim();
      }
      if (item.state && item.state.trim()) {
        customerState = item.state.trim();
      }
      if (item.zipCode && item.zipCode.trim()) {
        customerZipCode = item.zipCode.trim();
      }
      if (item.licensePlate && item.licensePlate.trim()) {
        customerLicensePlate = item.licensePlate.trim();
      }
      
      // If we found all essential info, we can break early
      if (customerEmail && customerName && customerPhone && customerAddress) {
        break;
      }
    }

    console.log("[CartPage] Extracted complete customer info:", { 
      email: customerEmail, 
      name: customerName,
      phone: customerPhone,
      address: customerAddress,
      city: customerCity,
      state: customerState,
      zipCode: customerZipCode,
      licensePlate: customerLicensePlate,
      fromItemsCount: items.length 
    });

    return {
      email: customerEmail,
      name: customerName,
      phone: customerPhone,
      address: customerAddress,
      city: customerCity,
      state: customerState,
      zipCode: customerZipCode,
      licensePlate: customerLicensePlate
    };
  };

  const customerInfo = getCustomerInfo();

  const handleAddAnotherPermit = () => {
    navigate('/');
  };

  useEffect(() => {
    // Check URL parameters for payment status
    const queryParams = new URLSearchParams(window.location.search);
    const canceled = queryParams.get('canceled');
    const error = queryParams.get('error');
    
    if (canceled) {
      toast({
        title: "Payment Canceled",
        description: "Your payment was canceled. Your cart items are still saved.",
        variant: "destructive"
      });
      
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }

    if (error) {
      toast({
        title: "Payment Error",
        description: "There was an error processing your payment. Please try again.",
        variant: "destructive"
      });
      
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [toast]);

  return (
    <div className="min-h-screen bg-tribal-light">
      <DualHeader />
      
      <div className="container max-w-4xl mx-auto py-6 px-4 overflow-hidden">
        <CartHeader />
        
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {items.length === 0 ? (
          <EmptyCart />
        ) : (
          <div className="space-y-4">
            <div className="space-y-4 mb-6 max-w-full">
              {items.map((item) => (
                <CartItem
                  key={item.id}
                  item={item}
                  onRemove={handleRemoveItem}
                  isExpanded={expandedItems.includes(item.id)}
                  onToggleExpand={() => toggleItemDetails(item.id)}
                />
              ))}
            </div>
            
            <OrderSummary
              items={items}
              total={getTotal()}
              onCheckout={async () => {
                try {
                  setError(null);
                  
                  // Validate customer info before proceeding
                  if (!customerInfo.email || !customerInfo.name) {
                    setError("Customer information is missing. Please ensure all items have valid customer details.");
                    return;
                  }

                  console.log("[CartPage] Starting checkout with complete customer info:", customerInfo);
                  await handleCheckout(items, customerInfo);
                } catch (error) {
                  const errorMessage = error instanceof Error 
                    ? error.message 
                    : "An unknown error occurred during checkout";
                  setError(errorMessage);
                  console.error("Checkout error:", error);
                }
              }}
              onClearCart={clearCart}
              isSubmitting={isProcessing}
              customerInfo={customerInfo}
            />

            {/* Add Another Permit button - positioned below the order summary */}
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                size="lg"
                onClick={handleAddAnotherPermit}
                className="bg-white border-tribal-neutral text-tribal-secondary hover:bg-tribal-light"
              >
                <Plus className="mr-1 h-4 w-4" />
                Add Another Permit
              </Button>
            </div>
          </div>
        )}

        {/* Add the assistance footer */}
        <AssistanceFooter />

        {debugInfo && (
          <div className="mt-4 p-2 bg-red-50 text-xs border border-red-100 rounded overflow-auto max-h-[200px]">
            <div className="font-semibold">Debug Information:</div>
            <pre className="overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
